import { useIsMobile } from "@/hooks/use-mobile";
import { billingHistoryStatus, cn, formatAmount, toApiSort } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { useGetBillingCategoryByCommunity, useGetBillingHistoryLogs } from "@/services";
import moment from "moment";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import {
  BILLING_RUN_DATE,
  JOB_RUN_STATUS,
  transactionTypeValues,
} from "../consts";
import {
  Badge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui";
import { useLocation } from "react-router-dom";
import { BadgeInfo } from "lucide-react";

const useListing = () => {
  const fromDashboard = useLocation().pathname?.includes("/dashboard");
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "updatedAt", desc: true }]);
  const [month, setMonth] = useState<number | string>(moment().format("MM"));
  const [monthKey] = useState<number>(Math.random() * 2);
  const isMobile = useIsMobile();
  const form = useForm<any>({
    defaultValues: {
      [BILLING_RUN_DATE]: "",
      [JOB_RUN_STATUS]: "",
    },
  });

  const {
    communityData: communityDetail,
    loggedInUserData,
    propertyData,
    pendingBillingAmt,
    invoiceDueDate,
  } = useSelector((state: RootState) => state.currentData);

  const { data: billingCategory, isLoading: isLoadingBillingCategory } =
  useGetBillingCategoryByCommunity({communityId: communityDetail?.communityId});

  const {
    data: documentList,
    isLoading: isDocLoading,
    refetch: refetchDoc,
    isRefetching: isDocRefetching,
  } = useGetBillingHistoryLogs({
    communityId: communityDetail?.communityId,
    data: {
      filters: [
        {
          field: "communityId",
          value: communityDetail?.communityId,
          operator: "EQUAL",
        },
        {
          field: "userId",
          value: loggedInUserData?.userId,
          operator: "EQUAL",
        },
        ...(invoiceDueDate
          ? [
              {
                field: "billingRunDate",
                value: moment().subtract(30, "days").format("YYYY-MM-DD"),
                operator: "LESS_THAN",
              },
            ]
          : []),
        ...(propertyData
          ? [
              {
                field: "propertyId",
                value: propertyData?.propertyId,
                operator: "EQUAL",
              },
              ...(fromDashboard
                ? [
                    {
                      field: JOB_RUN_STATUS,
                      value: "PENDING",
                      operator: "CONTAINS",
                    },
                  ]
                : []),
            ]
          : []),
        ...(form.watch()[BILLING_RUN_DATE]?.from &&
        form.watch()[BILLING_RUN_DATE]?.to
          ? [
              {
                field: BILLING_RUN_DATE,
                value: [
                  moment(form.watch()[BILLING_RUN_DATE]?.from).format(
                    "YYYY-MM-DD"
                  ),
                  moment(form.watch()[BILLING_RUN_DATE]?.to).format(
                    "YYYY-MM-DD"
                  ),
                ],
                operator: "BETWEEN",
              },
            ]
          : []),
        ...(form.watch()[JOB_RUN_STATUS] &&
        form.watch()[JOB_RUN_STATUS] !== "all"
          ? [
              {
                field: JOB_RUN_STATUS,
                value: form.watch()[JOB_RUN_STATUS],
                operator: "CONTAINS",
              },
            ]
          : []),
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "updatedAt", direction: "DESC" }),
    },
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  useEffect(() => {
    refetchDoc();
  }, [pageNumber, pageSize, sorting]);

  useEffect(() => {
    if (globalFilter?.length >= 2 || globalFilter?.length == 0) {
      refetchDoc();
    }
  }, [globalFilter]);

  const onSubmit = () => {
    refetchDoc();
  };

  const onClear = () => {
    form.reset();
    setTimeout(() => refetchDoc(), 200);
  };

  const outstandingColumns = [
    {
      accessorKey: "propertyName",
      header: <div>Property Name</div>,
    },
    {
      accessorKey: "propertyOwnershipId",
      header: <div>Property Ownership ID</div>,
    },
  ];

  // Define Table Columns
  const columns = [
    {
      accessorKey: "transactionType",
      header: <div>Transaction Type</div>,
      accessorFn: (row: any) =>
        (transactionTypeValues as any)[String(row?.transactionType || "")],
    },
    {
      accessorKey: "billingCategory",
      header: <div>Billing Category</div>,
    },
    {
      accessorKey: "billingRunDate",
      header: <div>Billing Date</div>,
      accessorFn: (row: any) =>
        row?.billingRunDate
          ? moment(row?.billingRunDate || "").format("MM/DD/YYYY")
          : "",
    },
    {
      accessorKey: "billingMonth",
      header: <div>Billing Month</div>,
      accessorFn: (row: any) =>
        row?.billingRunDate
          ? moment(row?.billingRunDate || "").format("MMM")
          : "",
    },
    {
      accessorKey: "billingAmount",
      meta: { align: "right" },
      header: <div>Billing Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.billingAmount || 0),
    },
    ...(fromDashboard && invoiceDueDate ? outstandingColumns : []),
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: (row: any) => {
        return (
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={cn(
                billingHistoryStatus[row?.row?.original?.status?.toLowerCase()]
                  ?.color,
                "rounded-full px-4 py-2"
              )}
            >
              {
                billingHistoryStatus[row?.row?.original?.status?.toLowerCase()]
                  ?.label
              }
            </Badge>

            {fromDashboard &&
            pendingBillingAmt == 0 &&
            row?.row?.original?.status?.toLowerCase() == "pending" ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="mx-1" type="button">
                    <BadgeInfo className="h-5 w-5 stroke-yellow-600" />
                  </TooltipTrigger>
                  <TooltipContent>
                    {`Your payment has been received and is currently under manual verification.`}
                    <br />
                    {`This process typically takes 2–3 days.`} <br />
                    {`Once verified, the status will be updated to ‘Completed`}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              ""
            )}
          </div>
        );
      },
    },
  ];

  const onRowClick = () => {
    // navigate(`${row.original.parkedDocumentHeaderId}/view`);
  };

  const onMonthChange = (val: any) => {
    setMonth(val);
    setTimeout(() => {
      refetchDoc();
    }, 100);
  };

  return {
    columns,
    globalFilter,
    setGlobalFilter,
    isMobile,
    onRowClick,
    isDocLoading,
    documentList,
    isDocRefetching,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    form,
    onSubmit,
    onClear,
    communityDetail,
    onMonthChange,
    monthKey,
    month,
    isLoadingBillingCategory,
    billingCategory,
    sorting,
    setSorting,
  };
};

export default useListing;
